import { ClustererOptions } from "@react-google-maps/marker-clusterer";
import React, { FC, useCallback, useState } from "react";
import {
  GoogleMap,
  useLoadScript,
  MarkerClusterer,
} from "@react-google-maps/api";
import MapMarker from "./MapMarker";
import { isBrowser } from "../../../helpers";
import {
  customZoomControl,
  initCustomZoomControlMapHandlers,
} from "./customZoomControl";

export type TCoordinate = {
  lat: number;
  lng: number;
};

export interface IMapProps {
  defaultCenter: TCoordinate;
  defaultZoom: number;
  places?: any;
  place?: TCoordinate;
  mapContainerStyle?: Record<string, unknown>;
  mapOptions?: google.maps.MapOptions;
  clusterOptions?: ClustererOptions;
  googleMapsApiKey?: string;
  onMarkerClick?(id: number): void;
}

export const DEFAULT_GOOGLE_MAP_OPTIONS: google.maps.MapOptions = {
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  zoomControl: false,
};

export const DEFAULT_GOOGLE_MAP_CLUSTER_OPTIONS: ClustererOptions = {
  styles: [
    {
      url: "assets/images/map-cluster/m1.svg",
      textColor: "white",
      height: 24,
      width: 24,
    },
  ],
};

const MapCanvas: FC<IMapProps> = ({
  defaultCenter,
  defaultZoom,
  places,
  place,
  mapContainerStyle,
  clusterOptions = DEFAULT_GOOGLE_MAP_CLUSTER_OPTIONS,
  mapOptions = DEFAULT_GOOGLE_MAP_OPTIONS,
  googleMapsApiKey,
  onMarkerClick,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey:
      googleMapsApiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [selectedMarker, setSelectedMarker] = useState<number>(null);

  const onSelectMarker = useCallback(
    (marker: number) => {
      setSelectedMarker(marker);
      onMarkerClick && onMarkerClick(marker);
    },
    [onMarkerClick],
  );

  const onLoad = useCallback((mapInstance) => {
    if (isBrowser()) {
      const zoomControlDiv = document.createElement("div");
      zoomControlDiv.setAttribute("data-testid", "ZoomControl");
      const { zoomInButton, zoomOutButton } = customZoomControl(zoomControlDiv);
      initCustomZoomControlMapHandlers({
        zoomInButton,
        zoomOutButton,
        map: mapInstance,
      });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      zoomControlDiv.index = 1;
      mapInstance.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(
        zoomControlDiv,
      );
    }
  }, []);

  if (loadError) {
    return (
      <div className="p-4 text-color-red text-center">
        Map cannot be loaded right now, sorry.
      </div>
    );
  }

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={
        mapContainerStyle || {
          height: "100vh",
          width: "100%",
        }
      }
      zoom={defaultZoom}
      center={defaultCenter}
      options={mapOptions}
      onLoad={onLoad}
      data-testid="map-wrapper"
    >
      {places && (
        <MarkerClusterer options={clusterOptions}>
          {(clusterer) =>
            places?.map((marker) => (
              <MapMarker
                key={marker.id}
                id={marker.id}
                location={marker.location}
                clusterer={clusterer}
                selectedLocation={selectedMarker}
                onSelectLocation={onSelectMarker}
              />
            ))
          }
        </MarkerClusterer>
      )}
      {place && (
        <MapMarker
          location={place}
          selectedLocation={selectedMarker}
          onSelectLocation={onSelectMarker}
        />
      )}
    </GoogleMap>
  ) : (
    <div className="p-4 text-center">Loading maps...</div>
  );
};

export default MapCanvas;
